import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@linaria/react';
import isEmpty from 'lodash/isEmpty.js';
import { useForm } from 'react-hook-form';
import { Button as _Button, COLORS, Input as _Input, typographyStyles } from '@livecommerce/design-system';
import { checkImageSizeIsLessThanLimit, cropImageWithAspectRatio } from 'helpers/imageHelper.jsx';
import { Loader } from '@bambuser-ui';
import ProfileImage from 'components-v3/ProfileImage/index.jsx';
import { useUser } from 'contexts/UserContext.jsx';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import { useCallSettings, useNotifications } from 'hooks/index.js';
import { ROLES } from 'shared-permissions';
import LinkResource from './LinkResource/index.jsx';
import { trackingService } from 'services/tracking/TrackingService.js';

export const { getBodyText } = typographyStyles;

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 214px;
  grid-template-rows: 1fr;
  column-gap: 32px;
  padding-top: 24px;
`;

const Button = styled(_Button)`
  position: relative;
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: flex-end;

  ${Button} {
    margin-right: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const Form = styled.form`
  grid-area: 1 / 1 / 2 / 2;
`;

const Avatar = styled.div`
  grid-area: 1 / 2 / 2 / 3;
  display: flex;
  flex-direction: column;

  > * {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const ProfileImageWrapper = styled.div`
  position: relative;
`;

const ProfileImageOverlay = styled.div`
  position: absolute;
  opacity: 0;
  background-color: rgba(255, 255, 255, 0.4);
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  &:hover {
    opacity: 1;
  }
`;

const RemoveProfileImageButton = styled(_Button)`
  position: absolute;
  top: 20px;
  right: 20px;
`;

const HiddenInput = styled.input`
  display: none;
`;

const UpdatePhotoDescription = styled.p`
  ${getBodyText(12)};
  color: ${COLORS.greyMediumDark};
  text-align: center;
`;

const Input = styled(_Input)`
  padding-bottom: 8px;
`;

const UploadLabel = styled.span`
  visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
`;

const CalendarIdWrapper = styled.div`
  margin-bottom: 24px;
`;

const CalendarIDDescription = styled.p`
  ${getBodyText(12)};
`;

const acceptedFileTypes = '.jpg, .jpeg, .png';

const isCallsAgent = (roles) => Array.isArray(roles) && roles.some((role) => ROLES.CALLS_AGENT === role);

const submitTrackingData = {
  eventType: 'profile-info:submit',
};

const clickRemoveAvatarTrackingData = {
  eventType: 'profile-info:click:avatar-remove',
};

const clickAvatarUploadTrackingData = {
  eventType: 'profile-info:click:avatar-upload',
};

const ProfileInfo = ({ setIsClosable }) => {
  const { t } = useLocalization();
  const { userData, updateUserData, supportedFields, avatarUploadEnabled } = useUser();
  const { avatar, displayName, fullName, jobTitle, calendarId, roles } = userData;
  const defaultValues = supportedFields.reduce((accumulatedFields, field) => ({ ...accumulatedFields, [field]: userData[field] }), {});
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
    watch,
    setValue,
  } = useForm({
    defaultValues,
  });

  const { handleFooterNotification, clearFooterNotification } = useNotifications();
  const { settings: { showCalendarId, useLinkResource: isUsingLinkResource, bookingSystem } } = useCallSettings();
  const { features } = useFeatureContext();
  const { callsEnabled } = features;
  const [loading, setLoading] = useState(false);
  const inputRef = useRef();

  const isNativeBooking = bookingSystem === 'native';
  const shouldDisplayCalendarId = isCallsAgent(roles) && callsEnabled && showCalendarId && !isNativeBooking && supportedFields.includes('calendarId');

  const handleResetClick = () => {
    trackingService.track({
      eventType: 'profile-modal:reset:profile-info',
    });
    reset({ fullName, displayName, jobTitle, calendarId });
  };

  useEffect(() => {
    reset({ fullName, displayName, jobTitle, calendarId });
  }, [fullName, displayName, jobTitle, calendarId, reset]);

  useEffect(() => {
    setIsClosable(!isDirty);
  }, [isDirty, setIsClosable]);

  const handleUpdatePhotoClick = () => {
    trackingService.track(clickAvatarUploadTrackingData);
    inputRef.current.click();
  };

  const handleRemovePhotoClick = async () => {
    trackingService.track(clickRemoveAvatarTrackingData);
    setLoading(true);

    const catchCallback = () => {
      trackingService.track({
        ...clickRemoveAvatarTrackingData,
        actionSuccess: false,
      });
    };

    await updateUserData({ avatar: null }, { catchCallback });
    trackingService.track({
      ...clickRemoveAvatarTrackingData,
      actionSuccess: true,
    });
    setLoading(false);
  };

  const handleFile = async ({ target: { files } }) => {
    clearFooterNotification();
    if (files.length === 0) {
      handleFooterNotification({
        type: 'error',
        message: t('profileImageUploadComponent.noFileSelectedNotificationBody'),
      });
      return;
    }
    if (!files[0].type.includes('image')) {
      handleFooterNotification({
        type: 'error',
        title: t('profileImageUploadComponent.fileIsNotAnImageNotificationTitle'),
        message: t('profileImageUploadComponent.fileIsNotAnImageNotificationBody'),
      });
      return;
    }
    const file = files[0];
    if (!checkImageSizeIsLessThanLimit(file)) {
      handleFooterNotification({
        type: 'error',
        title: t('profileImageUploadComponent.uploadMetaText'),
        message: t('profileImageUploadComponent.fileSizeTooBigErrorMessage'),
      });
      return;
    }
    setLoading(true);
    const image = await cropImageWithAspectRatio(file, 1);
    const formData = new FormData();
    formData.set('avatar', image);
    formData.set('timestamp', Date.now());

    const catchCallback = () => {
      trackingService.track({
        ...clickAvatarUploadTrackingData,
        actionSuccess: false,
      });
    };

    await updateUserData(
      {
        avatar: formData,
      },
      { catchCallback },
    );
    trackingService.track({
      ...clickAvatarUploadTrackingData,
      actionSuccess: true,
    });
    setLoading(false);
  };

  const onSubmit = async (data) => {
    trackingService.track(submitTrackingData);

    const catchCallback = () => {
      trackingService.track({
        ...submitTrackingData,
        actionSuccess: false,
      });
    };
    await updateUserData(
      {
        fullName: data.fullName,
        displayName: data.displayName,
        jobTitle: data.jobTitle,
        calendarId: data.calendarId,
      },
      { catchCallback },
    );
    trackingService.track({
      ...submitTrackingData,
      actionSuccess: true,
    });
  };

  const saveCalendarId = (newCalendarId) => {
    setValue('calendarId', newCalendarId, { shouldDirty: true });
  };

  return (
    <Container>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {
          supportedFields.includes('fullName') && (
            <Input
              placeholder={t('profile.fullNamePlaceholder')}
              hasHint
              label={t('profile.fullName')}
              {...register('fullName')}
            />
          )
        }
        {
          supportedFields.includes('displayName') && (
            <Input
              placeholder={t('profile.displayNamePlaceholder')}
              hasHint
              hint={errors.displayName?.message}
              label={t('profile.displayName')}
              status={errors.displayName?.message ? 'error' : 'normal'}
              {...register('displayName', { required: t('profile.displayNameRequiredError') })}
            />
          )
        }
        {
          supportedFields.includes('jobTitle') && (
            <Input placeholder={t('profile.jobTitlePlaceholder')} label={t('profile.jobTitle')} {...register('jobTitle')} />
          )
        }
        {shouldDisplayCalendarId && (
          <CalendarIdWrapper>
            <CalendarIDDescription>
              {isUsingLinkResource ? t('profile.linkResourceInfo') : t('profile.bookingsInfo')}
            </CalendarIDDescription>
            {isUsingLinkResource ? (
              <LinkResource saveCalendarId={saveCalendarId} calendarId={watch('calendarId')} />
            ) : (
              <Input placeholder={t('profile.changeCalendarIdPlaceholder')} {...register('calendarId')} />
            )}
          </CalendarIdWrapper>
        )}
        {isDirty && (
          <ActionsContainer>
            <Button disabled={isSubmitting} type="reset" variant="regular" onClick={handleResetClick}>
              {t('genericLabels.cancel')}
            </Button>
            <Button disabled={isSubmitting || !isEmpty(errors)} type="submit" variant="primary">
              {t('genericLabels.save')}
            </Button>
          </ActionsContainer>
        )}
      </Form>
      <Avatar>
        <ProfileImageWrapper>
          <ProfileImage width={214} height={214} alt="User Avatar" />
          {avatar && avatarUploadEnabled && (
            <ProfileImageOverlay>
              <RemoveProfileImageButton type="button" variant="primary" onClick={handleRemovePhotoClick}>
                {t('profileImageUploadComponent.modalUploadDeleteButton')}
              </RemoveProfileImageButton>
            </ProfileImageOverlay>
          )}
        </ProfileImageWrapper>
        {avatarUploadEnabled && (
          <>
            <Button disabled={loading} type="button" variant="regular" onClick={handleUpdatePhotoClick}>
              <Loader loading={loading} type="small" />
              <UploadLabel visible={!loading}>{t('profileImageUploadComponent.uploadButton')}</UploadLabel>
            </Button>
            <HiddenInput type="file" onChange={handleFile} accept={acceptedFileTypes} ref={inputRef} />
            <UpdatePhotoDescription>{t('profileImageUploadComponent.uploadMetaText')}</UpdatePhotoDescription>
          </>
        )}
      </Avatar>
    </Container>
  );
};

export default ProfileInfo;
